import { supabase } from '../lib/supabase';
import { getZoneDMApiInstance, ContactRequest, initializeZoneDMApi } from './zonedm';
import { initializeLeadConnectorApi, LeadConnectorContactRequest, normalizeCountryCode } from './leadConnector';

const LEAD_CONNECTOR_BASE = 'https://services.leadconnectorhq.com';
const LEAD_CONNECTOR_VERSION = '2021-07-28';

function normalizeTag(tag: string): string {
  return tag.trim().toLowerCase();
}

function mergeTags(existing: string[], desired: string[]): string[] {
  const merged: string[] = [];
  const seen = new Set<string>();
  const addTag = (tag?: string | null) => {
    if (!tag) return;
    const cleaned = String(tag).trim();
    if (!cleaned) return;
    const key = normalizeTag(cleaned);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(cleaned);
  };
  existing.forEach(addTag);
  desired.forEach(addTag);
  return merged;
}

async function getExistingContactTags(token: string, locationId: string, email?: string | null, phone?: string | null): Promise<string[]> {
  try {
    if (!token || !locationId) return [];
    if (!email && !phone) return [];
    const params = new URLSearchParams();
    params.append('locationId', locationId);
    if (email) params.append('email', email);
    if (phone) params.append('phone', phone);

    const res = await fetch(`${LEAD_CONNECTOR_BASE}/contacts/search?${params.toString()}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        Version: LEAD_CONNECTOR_VERSION,
        Accept: 'application/json',
      },
    });
    if (!res.ok) return [];
    const data: any = await res.json();
    const contact = Array.isArray(data?.contacts) ? data.contacts[0] : null;
    const tags = contact?.tags;
    return Array.isArray(tags) ? tags.filter(Boolean).map((t: any) => String(t)) : [];
  } catch {
    return [];
  }
}

/**
 * Helper function to safely convert external IDs to strings
 */
function safeExternalId(id: number | string | null | undefined): string {
  if (id === null || id === undefined) return "";
  return String(id);
}

/**
 * Generate a UUID for internal IDs
 */
function generateUUID(): string {
  if (typeof globalThis.crypto?.randomUUID === "function") {
    return globalThis.crypto.randomUUID();
  }
  // Fallback RFC4122 v4 approximation
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Process a webhook transaction and push the data to the appropriate GHL/ZoneDM integration
 */
export async function processWebhookTransaction(transactionId: string): Promise<boolean> {
  try {
    // 1. Try to get the transaction from emr_transactions first, fallback to old table name
    let { data: transaction, error: fetchError } = await supabase
      .from('emr_transactions')
      .select('*')
      .eq('id', transactionId)
      .maybeSingle();

    // If table doesn't exist (migration not applied), try old table name
    if (fetchError && (fetchError.code === '42P01' || fetchError.message?.includes('does not exist'))) {
      console.log('emr_transactions not found, falling back to fourdeemr_transactions...');
      const fallbackResult = await supabase
        .from('fourdeemr_transactions')
        .select('*')
        .eq('id', transactionId)
        .maybeSingle();
      transaction = fallbackResult.data;
      fetchError = fallbackResult.error;
    }

    // If not found in emr_transactions/fourdeemr_transactions, try webhook_logs
    if (!transaction) {
      const { data: webhookLog, error: webhookError } = await supabase
        .from('webhook_logs')
        .select('*')
        .eq('id', transactionId)
        .maybeSingle();

      if (webhookLog) {
        transaction = {
          id: webhookLog.id,
          user_id: null,
          type: webhookLog.type,
          payload: webhookLog.payload,
          status: webhookLog.status,
          error: null,
          created_at: webhookLog.created_at,
          processed_at: null
        };
      } else if (webhookError) {
        console.error('Error fetching from webhook_logs:', webhookError);
      }
    } else if (fetchError) {
      console.error('Error fetching from emr_transactions:', fetchError);
    }

    if (!transaction) {
      console.error(`Transaction with ID ${transactionId} not found in any table`);
      return false;
    }

    // We no longer update status to processing
    // This ensures transactions remain in their original 'received' state until completed or failed

    // 2. Extract the client ID from the payload
    const clientId = transaction.payload?.ClientId;
    if (!clientId) {
      await updateTransactionStatus(transactionId, 'failed', 'No client ID found in payload');
      return false;
    }

    // 3. Find the matching 4D EMR integration for this client ID
    const { data: fourDIntegrations, error: fourDIntegrationsError } = await supabase
      .from('integrations')
      .select('*')
      .eq('type', '4d')
      .or(`config->>clientId.eq.${clientId}`);

    if (fourDIntegrationsError) {
      console.error('Error fetching 4D integrations:', fourDIntegrationsError);
      await updateTransactionStatus(
        transactionId, 
        'failed', 
        `Error fetching 4D integrations: ${fourDIntegrationsError.message}`
      );
      return false;
    }

    if (!fourDIntegrations || fourDIntegrations.length === 0) {
      await updateTransactionStatus(
        transactionId, 
        'failed', 
        `No matching 4D EMR integration found for client ID: ${clientId}`
      );
      return false;
    }

    // 4. Now find the ZoneDM integration for the same user
    const userId = fourDIntegrations[0].user_id;
    
    const { data: zoneDMIntegrations, error: zoneDMIntegrationsError } = await supabase
      .from('integrations')
      .select('*')
      .eq('type', 'zonedm')
      .eq('user_id', userId);

    if (zoneDMIntegrationsError) {
      console.error('Error fetching ZoneDM integrations:', zoneDMIntegrationsError);
      await updateTransactionStatus(
        transactionId, 
        'failed', 
        `Error fetching ZoneDM integrations: ${zoneDMIntegrationsError.message}`
      );
      return false;
    }

    if (!zoneDMIntegrations || zoneDMIntegrations.length === 0) {
      await updateTransactionStatus(
        transactionId, 
        'failed', 
        `No ZoneDM integration found for user with 4D EMR client ID: ${clientId}`
      );
      return false;
    }

    const integration = zoneDMIntegrations[0];
    
    // 5. Process the transaction based on its type
    if (transaction.type === 'patient_new' || transaction.type === 'patient_update') {
      return await processPatientWebhook(transaction, integration, userId);
    } else {
      // For other transaction types, mark as completed without processing
      await updateTransactionStatus(transactionId, 'completed', 'No processing required for this transaction type');
      return true;
    }
  } catch (error) {
    console.error('Error processing webhook transaction:', error);
    await updateTransactionStatus(transactionId, 'failed', error instanceof Error ? error.message : String(error));
    return false;
  }
}

/**
 * Process a patient webhook and push the data to GHL/ZoneDM
 */
async function processPatientWebhook(transaction: any, integration: any, userId: string): Promise<boolean> {
  try {
    const patientData = transaction.payload;
    const transactionId = transaction.id;
    const clientId = patientData.ClientId;
    
    // Normalize IsNew for legacy support
    if (typeof patientData.IsNew !== 'boolean' && typeof patientData.isNew === 'boolean') {
      patientData.IsNew = patientData.isNew;
      delete patientData.isNew;
    }

    // Upsert patient to local patients table (matching scheduler logic)
    try {
      if (!patientData.PatientId) {
        console.warn(`Patient webhook missing PatientId, skipping database upsert`);
      } else {
        // Get or create external_id_mapping
        const externalId = safeExternalId(patientData.PatientId);
        const { data: existingMapping } = await supabase
          .from('external_id_mapping')
          .select('internal_id')
          .eq('user_id', userId)
          .eq('external_id', externalId)
          .eq('entity_type', 'patient')
          .maybeSingle();
        
        let internalId = existingMapping?.internal_id;
        
        // If no mapping exists, create it
        if (!internalId) {
          internalId = generateUUID();
          const { error: mappingError } = await supabase
            .from('external_id_mapping')
            .insert({
              user_id: userId,
              external_id: externalId,
              internal_id: internalId,
              entity_type: 'patient'
            });
          
          if (mappingError) {
            // Race condition: mapping might have been created by another process
            // Retry lookup to get the existing internal_id
            console.warn(`Mapping insert failed for patient ${externalId}, retrying lookup:`, mappingError.message);
            const { data: retryMapping } = await supabase
              .from('external_id_mapping')
              .select('internal_id')
              .eq('user_id', userId)
              .eq('external_id', externalId)
              .eq('entity_type', 'patient')
              .maybeSingle();
            
            if (retryMapping?.internal_id) {
              internalId = retryMapping.internal_id;
              console.log(`Found existing mapping for patient ${externalId} after retry`);
            } else {
              console.error(`Failed to create or find external_id_mapping for patient ${externalId}`);
              // Continue with generated UUID - patient upsert will still work
            }
          }
        }
        
        // Build patient row matching scheduler field mappings exactly
        const patientRow = {
          id: internalId,
          user_id: userId,
          external_id: patientData.PatientId,
          first_name: patientData.FirstName,
          last_name: patientData.LastName,
          gender: patientData.Gender,
          dob: patientData.DOB,
          address1: patientData.Address1,
          address2: patientData.Address2,
          city: patientData.City,
          state: patientData.State,
          zipcode: patientData.ZipCode,
          country: patientData.Country,
          phone_primary: patientData.PhonePrimary,
          email: patientData.Email,
          referral_category: patientData.ReferralCategory,
          marketing_source: patientData.MarketingSource,
          status: patientData.Status,
          nickname: patientData.NickName,
          other_last_name: patientData.OtherLastName,
          middle_initial: patientData.MiddleInitial,
          custom1: patientData.Custom1,
          custom2: patientData.Custom2,
          custom3: patientData.Custom3,
          profile_photo_id: patientData.ProfilePhotoId,
          coordinator_id: patientData.CoordinatorId,
          account_number: patientData.AccountNumber,
          external_created_at: patientData.CreatedDate,
          external_updated_at: patientData.ModifiedDate,
          zonedm_sync_status: 'received',
          last_sync: new Date().toISOString(),
        };
        
        // Upsert patient with same conflict resolution as scheduler
        const { error: upsertError } = await supabase
          .from('patients')
          .upsert(patientRow, { onConflict: 'user_id,external_id' });
        
        if (upsertError) {
          console.error(`Failed to upsert patient ${patientData.PatientId} to patients table:`, upsertError);
          // Continue anyway - we'll still try to push to ZoneDM
        } else {
          console.log(`Successfully upserted patient ${patientData.PatientId} to patients table`);
        }
      }
    } catch (dbError) {
      console.error('Error storing patient in database:', dbError);
      // Continue anyway - we'll still try to push to ZoneDM
    }

    // Get the locationId from the integration config
    // We need to use the locationId from the integration, not the clientId
    const locationId = integration.config.locationId;
    
    if (!locationId) {
      await updateTransactionStatus(
        transactionId, 
        'failed', 
        `Missing locationId in integration config for clientId: ${clientId}`
      );
      return false;
    }
    
    // Initialize the ZoneDM API with the integration credentials
    const credentials = {
      pit: integration.config.apiKey || integration.config.pit,
      locationId: locationId
    };

    // Check if credentials are valid
    if (!credentials.pit) {
      await updateTransactionStatus(
        transactionId, 
        'failed', 
        `Invalid ZoneDM credentials: missing API key`
      );
      return false;
    }

    const existingTags = await getExistingContactTags(
      credentials.pit,
      credentials.locationId,
      patientData.Email,
      patientData.PhonePrimary || patientData.Phone
    );
    const mergedTags = mergeTags(existingTags, ['Patient', patientData.Status || 'ACTIVE'].filter(Boolean));

    // Map the patient data to a GHL contact
    const contactData: LeadConnectorContactRequest = {
      firstName: patientData.FirstName,
      lastName: patientData.LastName,
      email: patientData.Email,
      phone: patientData.PhonePrimary || patientData.Phone || '',
      dateOfBirth: patientData.DOB,
      address1: patientData.Address1 || '',
      // address2 is handled in sanitizeContactData
      city: patientData.City,
      state: patientData.State,
      postalCode: patientData.ZipCode,
      country: normalizeCountryCode(patientData.Country) || 'US',
      tags: mergedTags,
      locationId: credentials.locationId,
      source: patientData.MarketingSource || '4D EMR Webhook',
      customFields: [
        { key: 'PatientId', field_value: patientData.PatientId?.toString() || '' },
        { key: 'ClientId', field_value: patientData.ClientId?.toString() || '' },
        { key: 'Gender', field_value: patientData.Gender || '' },
        { key: 'PhoneType', field_value: patientData.PhonePrimaryType || '' },
        { key: 'Status', field_value: patientData.Status || 'ACTIVE' },
        { key: 'ReferralCategory', field_value: patientData.ReferralCategory || 'UNKNOWN' },
        { key: 'MarketingSource', field_value: patientData.MarketingSource || '' },
        { key: 'IsInsurance', field_value: patientData.IsInsurance?.toString() || 'false' },
        { key: 'IsCosmetic', field_value: patientData.IsCosmetic?.toString() || 'false' },
        { key: 'IsMarketingEmailOK', field_value: patientData.IsMarketingEmailOK?.toString() || 'false' },
        { key: 'IsMarketingTextOK', field_value: patientData.IsMarketingTextOK?.toString() || 'false' },
        { key: 'LeadId', field_value: patientData.LeadId?.toString() || '' },
        { key: 'Source', field_value: '4D EMR Webhook' },
        // Add Address2 as a custom field if present
        ...(patientData.Address2 ? [{ key: 'Address2', field_value: patientData.Address2 }] : [])
      ]
    };

    // Initialize the Lead Connector API and try direct upsert first
    const leadConnectorApi = initializeLeadConnectorApi(credentials.pit);
    
    try {
      console.log('Attempting direct upsert with Lead Connector API for locationId:', credentials.locationId);
      console.log('Contact data:', JSON.stringify(contactData, null, 2));
      
      // Try the XHR method first for better error handling
      const result = await leadConnectorApi.upsertContactXHR(contactData);
      // Normalize IsNew for compatibility with both IsNew and new
      // @ts-expect-error: legacy API may return 'new' instead of 'IsNew'
      const IsNew = typeof result.IsNew === 'boolean' ? result.IsNew : (typeof result?.new === 'boolean' ? result.new : false);
      // Update the transaction status
      await updateTransactionStatus(
        transactionId, 
        'completed', 
        `Successfully ${IsNew ? 'created' : 'updated'} contact in ZoneDM with ID: ${result.contactId}`
      );
      
      return true;
    } catch (directError) {
      console.warn('Direct Lead Connector upsert failed, falling back to proxy:', directError);
      
      // Fall back to the ZoneDM proxy approach if direct method fails
      const zonedmApi = initializeZoneDMApi(credentials);
      
      // Convert contact data format for ZoneDM API if needed
      const zonedmContactData: ContactRequest = {
        ...contactData,
        customFields: contactData.customFields?.reduce((obj, field) => {
          if (field.key) obj[field.key] = field.field_value;
          return obj;
        }, {} as Record<string, string>) || {}
      };
      
      try {
        const result = await zonedmApi.upsertContact(zonedmContactData);
        // Normalize IsNew for compatibility with both IsNew and new
        // @ts-expect-error: legacy API may return 'new' instead of 'IsNew'
        const IsNew = typeof result.IsNew === 'boolean' ? result.IsNew : (typeof result?.new === 'boolean' ? result.new : false);
        // Update the transaction status
        await updateTransactionStatus(
          transactionId, 
          'completed', 
          `Successfully ${IsNew ? 'created' : 'updated'} contact in ZoneDM with ID: ${result.contactId} (via proxy)`
        );
        
        return true;
      } catch (proxyError) {
        // Capture and log the specific error from the proxy attempt
        const errorMessage = proxyError instanceof Error ? proxyError.message : String(proxyError);
        console.error('ZoneDM proxy upsert failed:', errorMessage);
        
        await updateTransactionStatus(transactionId, 'failed', `Lead Connector API error: ${errorMessage}`);
        return false;
      }
    }
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error('Error processing patient webhook:', errorMessage);
    await updateTransactionStatus(transaction.id, 'failed', `Error: ${errorMessage}`);
    return false;
  }
}

/**
 * Update the status of a transaction
 */
async function updateTransactionStatus(
  transactionId: string, 
  status: 'received' | 'processing' | 'completed' | 'failed', 
  message?: string
): Promise<void> {
  try {
    // Update data for emr_transactions table (which has an error column)
    const transactionUpdateData: any = { status };
    
    if (message) {
      transactionUpdateData.error = message;
    }
    
    if (status === 'completed' || status === 'failed') {
      transactionUpdateData.processed_at = new Date().toISOString();
    }
    
    // Try to update in emr_transactions first
    let { error: updateError } = await supabase
      .from('emr_transactions')
      .update(transactionUpdateData)
      .eq('id', transactionId);
    
    // If table doesn't exist, fallback to old table name
    if (updateError && (updateError.code === '42P01' || updateError.message?.includes('does not exist'))) {
      console.log('emr_transactions not found, falling back to fourdeemr_transactions...');
      const fallbackResult = await supabase
        .from('fourdeemr_transactions')
        .update(transactionUpdateData)
        .eq('id', transactionId);
      updateError = fallbackResult.error;
    }
      
    if (updateError) {
      console.log('Note: Could not update emr_transactions/fourdeemr_transactions, trying webhook_logs');
    }

    // For webhook_logs, try to check if the columns exist first
    try {
      // First, try to update just the status which should always be safe
      const { error: statusUpdateError } = await supabase
        .from('webhook_logs')
        .update({ status })
        .eq('id', transactionId);
      
      if (!statusUpdateError) {
        // If status update succeeded, try to update processed_at if applicable
        if (status === 'completed' || status === 'failed') {
          try {
            await supabase
              .from('webhook_logs')
              .update({ processed_at: new Date().toISOString() })
              .eq('id', transactionId);
          } catch (processedAtError) {
            console.log('Could not update processed_at on webhook_logs:', processedAtError);
          }
        }
        
        // Try to update error if applicable
        if (message) {
          try {
            await supabase
              .from('webhook_logs')
              .update({ error: message })
              .eq('id', transactionId);
          } catch (errorUpdateError) {
            console.log('Could not update error on webhook_logs:', errorUpdateError);
          }
        }
      } else {
        console.log('Could not update status on webhook_logs:', statusUpdateError);
      }
    } catch (webhookError) {
      console.error('Error updating webhook_logs:', webhookError);
    }
  } catch (error) {
    console.error('Error updating transaction status:', error);
  }
}
